import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Palette, Save, RotateCcw, Loader2, CheckCircle2, Mail, Trash2, Image as ImageIcon,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import BrandLogo from "../components/BrandLogo";
import MaintenanceCard from "../components/MaintenanceCard";
import MediaPicker from "../components/MediaPicker";
import AdminHeroBgPanel from "../components/AdminHeroBgPanel";

const FONT_OPTIONS = [
  { value: "sans", label: "Sans (Outfit + Plex)" },
  { value: "serif", label: "Serif (Source Serif Pro)" },
  { value: "mono", label: "Mono (IBM Plex Mono)" },
];

const PALETTES = [
  {
    id: "sky-blue",
    label: "Classic Sky Blue",
    accent_color: "#6CABDD",
    ink_color: "#0A0F17",
    body_font: "sans",
  },
  {
    id: "matchday-black",
    label: "Match Day Black",
    accent_color: "#FFDD00",
    ink_color: "#0A0F17",
    body_font: "sans",
  },
  {
    id: "retro-gold",
    label: "Retro Gold",
    accent_color: "#C5A14E",
    ink_color: "#2A1F12",
    body_font: "serif",
  },
  {
    id: "high-contrast",
    label: "Daylight High-Contrast",
    accent_color: "#0046A8",
    ink_color: "#000000",
    body_font: "sans",
  },
  {
    id: "terrace-green",
    label: "Terrace Green",
    accent_color: "#16A34A",
    ink_color: "#0F1B12",
    body_font: "mono",
  },
];

export default function AdminTheme() {
  const { user } = useAuth();
  const { theme, refresh } = useTheme();
  const [form, setForm] = useState(theme);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState("");
  const [mobileMenuPickerOpen, setMobileMenuPickerOpen] = useState(false);
  const [mobileMenuUploading, setMobileMenuUploading] = useState(false);

  // Upload-from-disk handler for the mobile menu background image.
  // Same /api/uploads endpoint used elsewhere; result URL goes straight
  // into `mobile_menu_bg_image` so the live preview + save bar pick it
  // up immediately.
  const uploadMobileMenuImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setMobileMenuUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd);
      const url = r.data?.url;
      if (url) setField("mobile_menu_bg_image")(url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setMobileMenuUploading(false);
      // Reset the input so the same file can be re-selected if needed.
      try { e.target.value = ""; } catch (_e) { /* ignore */ }
    }
  };

  useEffect(() => {
    setForm(theme);
  }, [theme]);

  const dirty =
    form.accent_color !== theme.accent_color ||
    form.ink_color !== theme.ink_color ||
    form.body_font !== theme.body_font ||
    (form.brand_name || "") !== (theme.brand_name || "") ||
    (form.brand_tagline || "") !== (theme.brand_tagline || "") ||
    (form.brand_logo_url || "") !== (theme.brand_logo_url || "") ||
    (form.brand_logo_url_mobile || "") !== (theme.brand_logo_url_mobile || "") ||
    (form.brand_header_banner_url || "") !== (theme.brand_header_banner_url || "") ||
    (form.brand_logo_size || "") !== (theme.brand_logo_size || "") ||
    (form.splash_header_size || "") !== (theme.splash_header_size || "") ||
    (form.ticker_speed || "") !== (theme.ticker_speed || "") ||
    Number(form.home_posts_count || 7) !== Number(theme.home_posts_count || 7) ||
    (form.brand_name_color || "") !== (theme.brand_name_color || "") ||
    (form.brand_tagline_color || "") !== (theme.brand_tagline_color || "") ||
    (form.brand_font || "") !== (theme.brand_font || "") ||
    (form.favicon_url || "") !== (theme.favicon_url || "") ||
    (form.tab_title_suffix || "") !== (theme.tab_title_suffix || "") ||
    (form.og_share_image || "") !== (theme.og_share_image || "") ||
    (form.social_instagram_url || "") !== (theme.social_instagram_url || "") ||
    (form.social_facebook_url || "") !== (theme.social_facebook_url || "") ||
    (form.social_x_url || "") !== (theme.social_x_url || "") ||
    (form.social_whatsapp_url || "") !== (theme.social_whatsapp_url || "") ||
    (form.affiliate_disclosure || "") !== (theme.affiliate_disclosure || "") ||
    (form.ticker_bg_color || "") !== (theme.ticker_bg_color || "") ||
    (form.brand_show_name !== false) !== (theme.brand_show_name !== false) ||
    (form.brand_show_tagline !== false) !== (theme.brand_show_tagline !== false) ||
    (form.install_button_visible !== false) !== (theme.install_button_visible !== false) ||
    (form.footer_title || "") !== (theme.footer_title || "") ||
    (form.footer_description || "") !== (theme.footer_description || "") ||
    (form.footer_kicker || "") !== (theme.footer_kicker || "") ||
    (form.footer_tagline || "") !== (theme.footer_tagline || "") ||
    (form.footer_tagline_url || "") !== (theme.footer_tagline_url || "") ||
    (form.page_bg_color || "") !== (theme.page_bg_color || "") ||
    (form.header_bg_color || "") !== (theme.header_bg_color || "") ||
    (form.footer_bg_color || "") !== (theme.footer_bg_color || "") ||
    (form.mobile_menu_bg_color || "") !== (theme.mobile_menu_bg_color || "") ||
    Number(form.mobile_menu_opacity ?? 100) !== Number(theme.mobile_menu_opacity ?? 100) ||
    (form.mobile_menu_bg_image || "") !== (theme.mobile_menu_bg_image || "") ||
    (form.body_color || "") !== (theme.body_color || "") ||
    (form.brand_logo_random === true) !== (theme.brand_logo_random === true) ||
    JSON.stringify(form.brand_logo_pool || []) !== JSON.stringify(theme.brand_logo_pool || []);

  const flashSaved = () => {
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  };

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    if (!dirty) return;
    setSaving(true);
    setError("");
    try {
      await api.put("/settings/theme", form);
      await refresh();
      flashSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset the whole theme to factory defaults?")) return;
    setResetting(true);
    setError("");
    try {
      await api.delete("/settings/theme");
      await refresh();
      flashSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]"
      data-testid="page-admin-theme"
    >
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 sm:px-8">
          <RouterLink
            to="/admin"
            className="inline-flex items-center gap-2 font-display text-sm font-semibold"
          >
            <ArrowLeft size={14} /> Admin
          </RouterLink>
          <span className="label-kicker text-[var(--brand-body)]">
            Theme · {user?.name || user?.email}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-12 sm:px-8">
        <div className="mb-8 border-b border-black/10 pb-6">
          <div className="flex items-center gap-2 text-brand-accent">
            <Palette size={16} strokeWidth={2.5} />
            <span className="label-kicker">Site theme</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Paint the whole site in two clicks.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-[var(--brand-body)]">
            These settings ripple through every page. Per-page heading colours
            (in <RouterLink to="/admin/page-content" className="underline decoration-[#6CABDD] underline-offset-4">Page headings</RouterLink>)
            override these for that one section.
          </p>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="admin-theme-error"
          >
            {error}
          </p>
        ) : null}

        <section className="mb-6" data-testid="admin-theme-palettes">
          <p className="label-kicker text-[var(--brand-body)]">Quick palettes</p>
          <p className="mt-1 text-xs text-[var(--brand-body)]">
            Tap to fill the fields below. Click <span className="font-bold">Save theme</span> to apply.
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            {PALETTES.map((p) => {
              const active =
                form.accent_color === p.accent_color &&
                form.ink_color === p.ink_color &&
                form.body_font === p.body_font;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      accent_color: p.accent_color,
                      ink_color: p.ink_color,
                      body_font: p.body_font,
                    })
                  }
                  data-testid={`palette-${p.id}`}
                  className={`group flex items-center gap-3 rounded-md border bg-white px-3 py-2 text-left transition-colors ${
                    active
                      ? "border-[#0A0F17] ring-2 ring-[#0A0F17]/15"
                      : "border-black/10 hover:border-[#0A0F17]"
                  }`}
                >
                  <span className="flex shrink-0 -space-x-2">
                    <span
                      aria-hidden
                      className="h-7 w-7 rounded-full ring-2 ring-white"
                      style={{ background: p.accent_color }}
                    />
                    <span
                      aria-hidden
                      className="h-7 w-7 rounded-full ring-2 ring-white"
                      style={{ background: p.ink_color }}
                    />
                  </span>
                  <span>
                    <span className="block font-display text-sm font-bold leading-tight">
                      {p.label}
                    </span>
                    <span className="block label-kicker text-[var(--brand-body)]">
                      {p.accent_color} · {p.body_font}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <MaintenanceCard setError={setError} />

        {/* Hero background image chooser — moved here from the main Admin
            dashboard so all visual/theme settings live in one place. */}
        <AdminHeroBgPanel />

        <form
          onSubmit={save}
          className="space-y-8 rounded-md border border-black/10 bg-white p-6"
        >
          <BrandingSection form={form} setForm={setForm} setError={setError} refresh={refresh} />

          <FooterSection form={form} setForm={setForm} />

          <div className="border-t border-black/10 pt-6">
            <p className="label-kicker text-[var(--brand-body)]">Site palette</p>
            <p className="mt-1 text-xs text-[var(--brand-body)]">
              Accent + ink ripple through every page via CSS variables. Body
              font swaps the typeface for paragraphs.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <ColorRow
              label="Accent colour"
              hint="Used for kickers, links, hover states. Default #6CABDD (Sky Blue)."
              value={form.accent_color}
              onChange={setField("accent_color")}
              testid="theme-accent"
            />
            <ColorRow
              label="Ink / headline colour"
              hint="Big titles, bold labels and strong text. Default #0A0F17 (near black). Flip to a light colour for dark-mode themes."
              value={form.ink_color}
              onChange={setField("ink_color")}
              testid="theme-ink"
            />
          </div>

          <ColorRow
            label="Body text colour"
            hint="Paragraphs, descriptions and muted labels. Default #475569 (slate grey). Pair a light body colour with a dark page background for a true dark mode."
            value={form.body_color}
            onChange={setField("body_color")}
            testid="theme-body-color"
          />

          <ColorRow
            label="Headline ticker background"
            hint="The scrolling news bar at the top of the home and news pages. Default #002d75 (deep sky-blue navy)."
            value={form.ticker_bg_color}
            onChange={setField("ticker_bg_color")}
            testid="theme-ticker-bg"
          />

          <div className="rounded-md border border-black/10 bg-white p-5 sm:max-w-md">
            <SelectRow
              label="Ticker scroll speed"
              hint="How fast the headlines scroll across the bar. 'Normal' matches the previous default."
              options={TICKER_SPEED_OPTIONS}
              value={form.ticker_speed || "normal"}
              onChange={setField("ticker_speed")}
              testid="theme-ticker-speed"
            />
          </div>

          <div className="rounded-md border border-black/10 bg-white p-5 sm:max-w-md">
            <p className="label-kicker text-[var(--brand-body)]">
              Posts on the homepage
            </p>
            <p className="mt-1 text-xs text-[var(--brand-body)]">
              Moved to the main Admin dashboard — see the "Homepage post
              count" panel there, with separate dropdowns for desktop and
              mobile.
            </p>
          </div>

          <ColorRow
            label="Page background colour"
            hint="The base background behind every public page. Default #FFFFFF (white). Pick a soft off-white or dark tone to change the whole site's feel."
            value={form.page_bg_color}
            onChange={setField("page_bg_color")}
            testid="theme-page-bg"
          />

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <ColorRow
              label="Header background colour"
              hint="Top nav bar. Leave blank to match the page background. Set a hex to pin it to a specific colour (keeps the frosted-glass blur on scroll)."
              value={form.header_bg_color}
              onChange={setField("header_bg_color")}
              testid="theme-header-bg"
            />
            <ColorRow
              label="Footer background colour"
              hint="Footer bar. Leave blank to match the page background, or pin a hex."
              value={form.footer_bg_color}
              onChange={setField("footer_bg_color")}
              testid="theme-footer-bg"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <ColorRow
              label="Mobile menu background colour"
              hint="The drawer that slides down when you tap the hamburger on mobile. Defaults to white."
              value={form.mobile_menu_bg_color}
              onChange={setField("mobile_menu_bg_color")}
              testid="theme-mobile-menu-bg"
            />
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">
                Mobile menu opacity
              </label>
              <div className="mt-2 flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={Number(form.mobile_menu_opacity ?? 100)}
                  onChange={(e) => setField("mobile_menu_opacity")(Number(e.target.value))}
                  data-testid="theme-mobile-menu-opacity"
                  className="flex-1 accent-[#0A0F17]"
                />
                <span className="w-12 text-right font-display text-sm font-bold tabular-nums">
                  {Number(form.mobile_menu_opacity ?? 100)}%
                </span>
              </div>
              <p className="mt-1 text-xs italic text-[var(--brand-body)]">
                0% = fully see-through. 100% = solid. Blur stays on either way so text underneath still looks soft.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="block label-kicker text-[var(--brand-body)]">
                Mobile menu background image (optional)
              </label>
              {form.mobile_menu_bg_image ? (
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <div
                    className="h-16 w-28 flex-shrink-0 rounded-md border border-black/10 bg-cover bg-center"
                    style={{ backgroundImage: `url("${form.mobile_menu_bg_image}")` }}
                    aria-label="Current mobile menu background image"
                  />
                  <div className="min-w-0 flex-1 truncate text-xs text-[var(--brand-body)]">
                    {form.mobile_menu_bg_image}
                  </div>
                  <button
                    type="button"
                    onClick={() => setMobileMenuPickerOpen(true)}
                    className="rounded-md border border-black/15 px-3 py-2 label-kicker hover:border-[#0A0F17]"
                    data-testid="theme-mobile-menu-image-change"
                  >
                    From library
                  </button>
                  <label
                    className={`cursor-pointer rounded-md border border-black/15 px-3 py-2 label-kicker hover:border-[#0A0F17] ${mobileMenuUploading ? "opacity-60" : ""}`}
                    data-testid="theme-mobile-menu-image-upload"
                  >
                    {mobileMenuUploading ? "Uploading…" : "Upload new"}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={mobileMenuUploading}
                      onChange={uploadMobileMenuImage}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setField("mobile_menu_bg_image")("")}
                    className="rounded-md border border-red-200 bg-red-50 px-3 py-2 label-kicker text-red-700 hover:border-red-600"
                    data-testid="theme-mobile-menu-image-remove"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMobileMenuPickerOpen(true)}
                    data-testid="theme-mobile-menu-image-pick"
                    className="inline-flex items-center gap-2 rounded-md border border-dashed border-black/20 px-4 py-3 label-kicker hover:border-[#0A0F17]"
                  >
                    <ImageIcon size={14} /> Pick from Media Library
                  </button>
                  <label
                    className={`cursor-pointer inline-flex items-center gap-2 rounded-md border border-dashed border-black/20 px-4 py-3 label-kicker hover:border-[#0A0F17] ${mobileMenuUploading ? "opacity-60" : ""}`}
                    data-testid="theme-mobile-menu-image-upload"
                  >
                    <ImageIcon size={14} /> {mobileMenuUploading ? "Uploading…" : "Upload new image"}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={mobileMenuUploading}
                      onChange={uploadMobileMenuImage}
                    />
                  </label>
                </div>
              )}
              <p className="mt-1 text-xs italic text-[var(--brand-body)]">
                When set, the image fills the menu (cover, centred). The colour above sits on top as a tint — drop the opacity to let more of the image through.
              </p>
            </div>
          </div>

          <SelectRow
            label="Body font"
            options={FONT_OPTIONS}
            value={form.body_font}
            onChange={setField("body_font")}
            testid="theme-body-font"
          />

          {/* Live preview block */}
          <div
            className="rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-6"
            data-testid="theme-preview"
          >
            <p
              className="label-kicker"
              style={{ color: form.accent_color }}
            >
              Live preview
            </p>
            <h2
              className="brand-headline mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl"
              style={{ color: form.ink_color }}
            >
              The Sky Blues march on.
            </h2>
            <p className="mt-2 max-w-xl text-sm text-[var(--brand-body)]">
              Body copy renders in your chosen font and inherits the heading
              scale via CSS variables. Click Save to apply across the site.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-black/5 pt-4">
            {savedFlash ? (
              <span className="inline-flex items-center gap-1 label-kicker text-[#16A34A]">
                <CheckCircle2 size={12} /> Saved
              </span>
            ) : null}
            <button
              type="button"
              onClick={reset}
              disabled={resetting || saving}
              data-testid="theme-reset"
              className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-1.5 label-kicker hover:border-[#0A0F17] disabled:opacity-60"
            >
              {resetting ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
              Reset to defaults
            </button>
            <button
              type="submit"
              disabled={!dirty || saving}
              data-testid="theme-save"
              className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save theme"}
            </button>
          </div>
        </form>

        <NotificationsSection />
      </main>
      <MediaPicker
        open={mobileMenuPickerOpen}
        onClose={() => setMobileMenuPickerOpen(false)}
        onSelect={(url) => {
          if (url) setField("mobile_menu_bg_image")(url);
          setMobileMenuPickerOpen(false);
        }}
        accept="image"
      />
    </div>
  );
}

function ColorRow({ label, hint, value, onChange, testid }) {
  const empty = !value;
  const valid = empty || /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);
  return (
    <div>
      <label className="label-kicker text-[var(--brand-body)]">{label}</label>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="color"
          value={!empty && valid ? value : "#6CABDD"}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-picker`}
          className="h-10 w-14 cursor-pointer rounded-md border border-black/15 bg-white p-1"
        />
        <input
          type="text"
          value={value || ""}
          placeholder="(inherit)"
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className={`block w-full rounded-md border bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17] ${
            valid ? "border-black/15" : "border-[#E63946]"
          }`}
        />
        {!empty ? (
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testid}-clear`}
            className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
          >
            Clear
          </button>
        ) : null}
      </div>
      {hint ? <p className="mt-1 text-xs text-[var(--brand-body)]">{hint}</p> : null}
    </div>
  );
}

function SelectRow({ label, hint, options, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="label-kicker text-[var(--brand-body)]">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint ? (
        <span className="mt-1 block text-xs text-[var(--brand-body)]">{hint}</span>
      ) : null}
    </label>
  );
}

const LOGO_SIZE_OPTIONS = [
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium (default)" },
  { value: "lg", label: "Large" },
  { value: "xl", label: "Extra large" },
  { value: "2xl", label: "2XL (huge)" },
  { value: "3xl", label: "3XL (stretches to the nav)" },
];

const SPLASH_HEADER_SIZE_OPTIONS = [
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large (default)" },
  { value: "xl", label: "Extra large" },
];

const TICKER_SPEED_OPTIONS = [
  { value: "slow", label: "Slow (90s)" },
  { value: "normal", label: "Normal — default (45s)" },
  { value: "fast", label: "Fast (25s)" },
  { value: "very_fast", label: "Very fast (12s)" },
];

const BRAND_FONT_OPTIONS = [
  { value: "display", label: "Display (Outfit) — default" },
  { value: "sans", label: "Sans (clean)" },
  { value: "serif", label: "Serif (classic)" },
  { value: "mono", label: "Mono (techy)" },
];

function BrandingSection({ form, setForm, setError, refresh }) {
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const fileRef = React.useRef(null);
  const mobileFileRef = React.useRef(null);
  const bannerFileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [uploadingMobile, setUploadingMobile] = React.useState(false);
  const [uploadingBanner, setUploadingBanner] = React.useState(false);

  const onPickLogo = () => fileRef.current?.click();
  const onPickMobileLogo = () => mobileFileRef.current?.click();
  const onPickBanner = () => bannerFileRef.current?.click();

  const uploadLogoFile = async (file, fieldName, setBusy) => {
    const isVideo = file.type.startsWith("video/");
    const limit = isVideo ? 25 * 1024 * 1024 : 8 * 1024 * 1024;
    if (file.size > limit) {
      setError(
        isVideo
          ? "Video too large — please keep under 25 MB."
          : "Image too large — please keep under 8 MB."
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd);
      const url = r.data?.url;
      if (url) setField(fieldName)(url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadLogoFile(file, "brand_logo_url", setUploading);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onMobileLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadLogoFile(file, "brand_logo_url_mobile", setUploadingMobile);
    if (mobileFileRef.current) mobileFileRef.current.value = "";
  };

  const onBannerFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadLogoFile(file, "brand_header_banner_url", setUploadingBanner);
    if (bannerFileRef.current) bannerFileRef.current.value = "";
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="label-kicker text-[var(--brand-body)]">Header & branding</p>
        <p className="mt-1 text-xs text-[var(--brand-body)]">
          Logo, brand name and tagline used in the top-left of every page. The
          logo always links back to the homepage.
        </p>
      </div>

      <div className="flex items-start gap-5 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
        <div data-testid="brand-logo-preview" className="shrink-0">
          <BrandLogo
            src={form.brand_logo_url}
            alt="Logo preview"
            className="h-20"
          />
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <p className="font-display text-sm font-bold">Desktop logo</p>
          <p className="text-xs text-[var(--brand-body)]">
            The uploaded file's shape decides the logo's shape — no cropping.
            Image (PNG / JPG / WebP / GIF, &lt; 8 MB) or video (MP4 / WebM /
            MOV, &lt; 25 MB — autoplays muted in a loop). Shown on tablet and
            desktop screens (≥640px).
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onPickLogo}
              disabled={uploading}
              data-testid="brand-logo-upload"
              className="inline-flex items-center gap-1.5 rounded-md border border-black/15 bg-white px-3 py-1.5 label-kicker hover:border-[#0A0F17]"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : null}
              {uploading ? "Uploading…" : "Upload new logo"}
            </button>
            {form.brand_logo_url && form.brand_logo_url !== "/last-logo.png" ? (
              <button
                type="button"
                onClick={() => setField("brand_logo_url")("/last-logo.png")}
                data-testid="brand-logo-revert"
                className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
              >
                Revert to default
              </button>
            ) : null}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={onLogoFile}
            data-testid="brand-logo-input"
          />
        </div>
      </div>

      <div className="flex items-start gap-5 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
        <div data-testid="brand-logo-mobile-preview" className="shrink-0">
          <BrandLogo
            src={form.brand_logo_url_mobile || form.brand_logo_url}
            alt="Mobile logo preview"
            className="h-20"
          />
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <p className="font-display text-sm font-bold">Mobile logo</p>
          <p className="text-xs text-[var(--brand-body)]">
            Optional — shown instead of the desktop logo below 640px
            (phones, PWA). Leave unset to keep using the desktop logo on
            mobile too. Same file rules as above.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onPickMobileLogo}
              disabled={uploadingMobile}
              data-testid="brand-logo-mobile-upload"
              className="inline-flex items-center gap-1.5 rounded-md border border-black/15 bg-white px-3 py-1.5 label-kicker hover:border-[#0A0F17]"
            >
              {uploadingMobile ? <Loader2 size={12} className="animate-spin" /> : null}
              {uploadingMobile ? "Uploading…" : form.brand_logo_url_mobile ? "Replace mobile logo" : "Upload mobile logo"}
            </button>
            {form.brand_logo_url_mobile ? (
              <button
                type="button"
                onClick={() => setField("brand_logo_url_mobile")("")}
                data-testid="brand-logo-mobile-clear"
                className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
              >
                Use desktop logo on mobile
              </button>
            ) : null}
          </div>
          <input
            ref={mobileFileRef}
            type="file"
            accept="image/*,video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={onMobileLogoFile}
            data-testid="brand-logo-mobile-input"
          />
        </div>
      </div>

      <LogoPoolSection form={form} setForm={setForm} setError={setError} />

      <div className="flex items-start gap-5 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
        <div
          data-testid="brand-banner-preview"
          className="h-14 w-40 shrink-0 overflow-hidden rounded-sm border border-black/10 bg-white"
        >
          {form.brand_header_banner_url ? (
            <img
              src={form.brand_header_banner_url}
              alt="Header banner preview"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] text-[var(--brand-body)]">
              No banner
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <p className="font-display text-sm font-bold">Header banner (desktop)</p>
          <p className="text-xs text-[var(--brand-body)]">
            Optional — on tablet/desktop screens (≥640px) this image
            stretches across the full width of the header, replacing the
            logo area entirely. The nav menu, admin link and hamburger
            still render on top of it, same position as always. Use a
            wide image (e.g. 1600×120) for the best crop — it's scaled to
            fill (object-cover), so tall/square images will crop top and
            bottom. Leave unset to keep showing the normal logo. Not
            shown on mobile (&lt;640px). Image only, &lt; 8 MB.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onPickBanner}
              disabled={uploadingBanner}
              data-testid="brand-banner-upload"
              className="inline-flex items-center gap-1.5 rounded-md border border-black/15 bg-white px-3 py-1.5 label-kicker hover:border-[#0A0F17]"
            >
              {uploadingBanner ? <Loader2 size={12} className="animate-spin" /> : null}
              {uploadingBanner ? "Uploading…" : form.brand_header_banner_url ? "Replace banner" : "Upload banner"}
            </button>
            {form.brand_header_banner_url ? (
              <button
                type="button"
                onClick={() => setField("brand_header_banner_url")("")}
                data-testid="brand-banner-clear"
                className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
              >
                Remove banner, show logo again
              </button>
            ) : null}
          </div>
          <input
            ref={bannerFileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onBannerFile}
            data-testid="brand-banner-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <TextRow
          label="Brand name (top-left, bold)"
          hint="Default 'LAST'. Keep it short — appears next to the logo."
          value={form.brand_name}
          onChange={setField("brand_name")}
          maxLength={60}
          testid="brand-name"
        />
        <TextRow
          label="Tagline (small text under brand name)"
          hint="Default 'Let's All Sing Together · CCFC'. Hidden on mobile."
          value={form.brand_tagline}
          onChange={setField("brand_tagline")}
          maxLength={120}
          testid="brand-tagline"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <SelectRow
          label="Logo size"
          options={LOGO_SIZE_OPTIONS}
          value={form.brand_logo_size || "md"}
          onChange={setField("brand_logo_size")}
          testid="brand-logo-size"
        />
        <SelectRow
          label="Brand font"
          options={BRAND_FONT_OPTIONS}
          value={form.brand_font || "display"}
          onChange={setField("brand_font")}
          testid="brand-font"
        />
        <SelectRow
          label="Splash header size"
          hint="Size of the logo + brand text on the coming-soon page."
          options={SPLASH_HEADER_SIZE_OPTIONS}
          value={form.splash_header_size || "lg"}
          onChange={setField("splash_header_size")}
          testid="splash-header-size"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <ToggleRow
          label="Show brand name"
          hint="Hide to leave just the logo in the header."
          checked={form.brand_show_name !== false}
          onChange={(v) => setField("brand_show_name")(v)}
          testid="brand-show-name"
        />
        <ToggleRow
          label="Show tagline"
          hint="The small italic line under the brand name."
          checked={form.brand_show_tagline !== false}
          onChange={(v) => setField("brand_show_tagline")(v)}
          testid="brand-show-tagline"
        />
        <ToggleRow
          label="Show 'Install LAST' button"
          hint="Always-visible in the footer when on. Tap launches the install flow on Chrome/Edge/Android, or a quick how-to on iOS/Safari/Firefox."
          checked={form.install_button_visible !== false}
          onChange={(v) => setField("install_button_visible")(v)}
          testid="install-button-visible"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <ColorRow
          label="Brand name colour"
          hint="Blank inherits the site ink colour."
          value={form.brand_name_color}
          onChange={setField("brand_name_color")}
          testid="brand-name-color"
        />
        <ColorRow
          label="Tagline colour"
          hint="Blank inherits the site ink colour."
          value={form.brand_tagline_color}
          onChange={setField("brand_tagline_color")}
          testid="brand-tagline-color"
        />
      </div>

      <div className="border-t border-black/10 pt-5">
        <p className="label-kicker text-[var(--brand-body)]">Browser tab branding</p>
        <p className="mt-1 text-xs text-[var(--brand-body)]">
          The tiny icon in browser tabs/bookmarks (favicon) and the suffix used
          in tab titles. e.g. with suffix &ldquo;LAST&rdquo; the news page tab
          will read &ldquo;News · LAST&rdquo;.
        </p>
      </div>

      <div className="flex items-start gap-5 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
        <img
          src={form.favicon_url || "/last-logo.png"}
          alt="Favicon preview"
          data-testid="favicon-preview"
          className="h-10 w-10 rounded-sm object-cover ring-1 ring-black/10"
        />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="font-display text-sm font-bold">Favicon</p>
          <p className="text-xs text-[var(--brand-body)]">
            Square PNG / ICO works best. Tiny (browsers render it at 16–32px).
            Often the same as your logo.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <FaviconUploadButton
              onUploaded={(url) => setField("favicon_url")(url)}
              setError={setError}
            />
            {form.favicon_url && form.favicon_url !== "/last-logo.png" ? (
              <button
                type="button"
                onClick={() => setField("favicon_url")("/last-logo.png")}
                data-testid="favicon-revert"
                className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
              >
                Revert to default
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                const url = form.brand_logo_url || "";
                if (/\.(mp4|webm|mov)(\?|#|$)/i.test(url)) {
                  setError("Brand logo is a video — favicons need an image. Upload an image favicon below.");
                  return;
                }
                setField("favicon_url")(url);
              }}
              data-testid="favicon-match-logo"
              className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
            >
              Match brand logo
            </button>
          </div>
        </div>
      </div>

      <ShareImageBlock form={form} setField={setField} setError={setError} />

      <SocialLinksBlock form={form} setField={setField} />

      <AffiliateDisclosureBlock form={form} setField={setField} />

      <TextRow
        label="Tab title suffix"
        hint='Appears at the end of every tab title. Blank uses "LAST".'
        value={form.tab_title_suffix}
        onChange={setField("tab_title_suffix")}
        maxLength={40}
        testid="tab-title-suffix"
      />

      <PwaIconSection
        form={form}
        setForm={setForm}
        setError={setError}
        refresh={refresh}
      />
    </div>
  );
}

function LogoPoolSection({ form, setForm, setError }) {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const pool = Array.isArray(form.brand_logo_pool) ? form.brand_logo_pool : [];
  const random = !!form.brand_logo_random;
  const MAX_POOL = 25;

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    if (pool.length + files.length > MAX_POOL) {
      setError(`Logo pool is capped at ${MAX_POOL} images. Remove some before adding more.`);
      return;
    }
    setUploading(true);
    setError("");
    const next = [...pool];
    for (const file of files) {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        setError("Random-logo pool only supports images (PNG / JPG / WebP / GIF).");
        continue;
      }
      if (file.size > 8 * 1024 * 1024) {
        setError("Image too large — please keep each logo under 8 MB.");
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/uploads", fd);
        const url = r.data?.url;
        if (url) next.push(url);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    }
    setField("brand_logo_pool")(next);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeAt = (idx) => {
    const next = pool.filter((_, i) => i !== idx);
    setField("brand_logo_pool")(next);
  };

  return (
    <div className="rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-bold">Random logo pool</p>
          <p className="mt-1 text-xs text-[var(--brand-body)]">
            Upload a few logos and we'll show a random one from this pool
            each time someone loads the site. Turn the toggle off to go
            back to the single logo above. Pool images must be PNG / JPG /
            WebP / GIF (videos aren't supported for the random pool).
          </p>
        </div>
        <label className="flex items-center gap-2 rounded-md border border-black/10 bg-white px-3 py-2 text-sm shrink-0">
          <span className="font-display font-bold">Random</span>
          <button
            type="button"
            role="switch"
            aria-checked={random}
            onClick={() => setField("brand_logo_random")(!random)}
            data-testid="brand-logo-random-toggle"
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
              random ? "bg-[#0A0F17]" : "bg-black/15"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                random ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </button>
        </label>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || pool.length >= MAX_POOL}
          data-testid="brand-logo-pool-upload"
          className="inline-flex items-center gap-1.5 rounded-md border border-black/15 bg-white px-3 py-1.5 label-kicker hover:border-[#0A0F17] disabled:opacity-50"
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : null}
          {uploading ? "Uploading…" : "Add logos to pool"}
        </button>
        <span className="label-kicker text-[var(--brand-body)]">
          {pool.length} / {MAX_POOL}
        </span>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(Array.from(e.target.files || []))}
          data-testid="brand-logo-pool-input"
        />
      </div>

      {pool.length === 0 ? (
        <p className="mt-4 rounded-md border border-dashed border-black/10 p-4 text-center text-xs text-[var(--brand-body)]">
          No pool logos yet. Add a few above to enable the random rotation.
        </p>
      ) : (
        <ul
          className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-5"
          data-testid="brand-logo-pool-list"
        >
          {pool.map((url, i) => (
            <li
              key={`${url}-${i}`}
              className="group relative aspect-square overflow-hidden rounded-md border border-black/10 bg-white"
              data-testid={`brand-logo-pool-item-${i}`}
            >
              <BrandLogo src={url} alt="" className="h-full w-full object-contain p-2" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                data-testid={`brand-logo-pool-remove-${i}`}
                aria-label="Remove from pool"
                className="absolute right-1 top-1 rounded-full bg-[#0A0F17]/85 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {random && pool.length === 0 ? (
        <p className="mt-3 rounded-sm border-l-2 border-[#F59E0B] bg-[#F59E0B]/10 px-3 py-2 text-xs">
          Random is on but the pool is empty — the header will keep showing the
          single logo above until you upload at least one pool image.
        </p>
      ) : null}
    </div>
  );
}


function PwaIconSection({ form, setForm, setError, refresh }) {
  const anyUrl = form.pwa_icon_any || "";
  const maskUrl = form.pwa_icon_maskable || "";
  const hasCustom = Boolean(anyUrl || maskUrl);

  const onReset = async () => {
    if (!window.confirm("Revert PWA install icon to the default LAST logo?")) return;
    try {
      await api.delete("/admin/pwa-icon");
      setForm((f) => ({ ...f, pwa_icon_any: "", pwa_icon_maskable: "" }));
      // Same as upload — push the new (empty) icon URLs into ThemeContext
      // so admin pages don't keep showing the previous custom icon.
      refresh().catch(() => {});
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="border-t border-black/10 pt-5" data-testid="pwa-icon-section">
      <p className="label-kicker text-[var(--brand-body)]">Install app icon</p>
      <p className="mt-1 text-xs text-[var(--brand-body)]">
        The icon shown when fans tap <em>Install LAST</em> and pin the site to
        their home screen. Square images work best. We auto-generate a
        <em> maskable</em> version (with safe padding + your brand background)
        so Android can crop it into a circle without clipping the artwork.
      </p>

      <div className="mt-4 flex items-start gap-5 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
        <div className="flex shrink-0 items-center gap-3">
          <div className="text-center">
            <img
              src={anyUrl || "/last-logo.png"}
              alt="PWA icon — square"
              data-testid="pwa-icon-any-preview"
              className="h-16 w-16 rounded-sm object-cover ring-1 ring-black/10"
            />
            <p className="mt-1 text-[10px] uppercase tracking-wider text-[var(--brand-body)]">
              Square
            </p>
          </div>
          <div className="text-center">
            <img
              src={maskUrl || "/last-logo.png"}
              alt="PWA icon — masked"
              data-testid="pwa-icon-mask-preview"
              className="h-16 w-16 rounded-full object-cover ring-1 ring-black/10"
            />
            <p className="mt-1 text-[10px] uppercase tracking-wider text-[var(--brand-body)]">
              Masked
            </p>
          </div>
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <p className="font-display text-sm font-bold">Home-screen icon</p>
          <p className="text-xs text-[var(--brand-body)]">
            Upload one image — we'll resize, generate the maskable variant, and
            push it to every device that has installed (or installs) LAST.
            Recommended: 1024×1024 PNG with transparent background.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <PwaIconUploadButton
              onUploaded={(any, mask) => {
                setForm((f) => ({
                  ...f,
                  pwa_icon_any: any,
                  pwa_icon_maskable: mask,
                }));
                // The upload endpoint writes straight to the theme doc, so
                // we refresh the global ThemeContext too — otherwise the
                // form preview shows the new icon but every other page
                // (and the form on reopen) still sees the cached old URL.
                refresh().catch(() => {});
              }}
              setError={setError}
            />
            {hasCustom ? (
              <button
                type="button"
                onClick={onReset}
                data-testid="pwa-icon-revert"
                className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
              >
                Revert to default
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function PwaIconUploadButton({ onUploaded, setError }) {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError("Icon too large — please keep under 8 MB.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/pwa-icon", fd);
      const any = r.data?.pwa_icon_any;
      const mask = r.data?.pwa_icon_maskable;
      if (any && mask) onUploaded(any, mask);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onPick}
        disabled={uploading}
        data-testid="pwa-icon-upload"
        className="inline-flex items-center gap-1.5 rounded-md border border-black/15 bg-white px-3 py-1.5 label-kicker hover:border-[#0A0F17]"
      >
        {uploading ? <Loader2 size={12} className="animate-spin" /> : null}
        {uploading ? "Uploading…" : "Upload install icon"}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/png, image/jpeg, image/webp"
        className="hidden"
        onChange={onFile}
        data-testid="pwa-icon-input"
      />
    </>
  );
}

function ShareImageBlock({ form, setField, setError }) {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const url = form.og_share_image || "";

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      setError("Share image too large — please keep under 4 MB.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd);
      const uploaded = r.data?.url;
      if (uploaded) setField("og_share_image")(uploaded);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex items-start gap-5 rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
      <div
        className="hidden h-16 w-32 shrink-0 overflow-hidden rounded-sm bg-[#0A0F17] ring-1 ring-black/10 sm:block"
        aria-hidden="true"
      >
        {url ? (
          <img
            src={url}
            alt="Share image preview"
            data-testid="og-share-preview"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] font-bold uppercase tracking-wider text-white/60">
            Auto
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <p className="font-display text-sm font-bold">
          Social share image (Facebook / WhatsApp / Twitter)
        </p>
        <p className="text-xs text-[var(--brand-body)]">
          Shown as the link preview when someone shares your site. Ideal size{" "}
          <strong>1200&times;630&nbsp;px (1.91:1)</strong>. Leave blank and we
          auto-generate a 1200&times;630 frame using your header logo on a
          brand-coloured background — which is what most fan sites want.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onPick}
            disabled={uploading}
            data-testid="og-share-upload"
            className="inline-flex items-center gap-1.5 rounded-md border border-black/15 bg-white px-3 py-1.5 label-kicker hover:border-[#0A0F17]"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : null}
            {uploading ? "Uploading…" : url ? "Replace image" : "Upload image"}
          </button>
          {url ? (
            <button
              type="button"
              onClick={() => setField("og_share_image")("")}
              data-testid="og-share-clear"
              className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
            >
              Use auto-generated logo card
            </button>
          ) : null}
        </div>
        <p className="text-[11px] leading-snug text-[var(--brand-body)]">
          After you save, paste{" "}
          <code className="rounded bg-black/5 px-1 py-0.5">
            https://letsallsingtogether.com/
          </code>{" "}
          into{" "}
          <a
            href="https://developers.facebook.com/tools/debug/"
            target="_blank"
            rel="noreferrer"
            className="underline decoration-[#6CABDD] underline-offset-2"
          >
            Facebook&apos;s Sharing Debugger
          </a>{" "}
          and tap <em>Scrape Again</em> so FB / WhatsApp pick up the new image.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="image/png, image/jpeg, image/webp"
          className="hidden"
          onChange={onFile}
          data-testid="og-share-input"
        />
      </div>
    </div>
  );
}


function AffiliateDisclosureBlock({ form, setField }) {
  const value = form.affiliate_disclosure ?? "";
  const empty = !value.trim();
  return (
    <div className="rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4" data-testid="admin-affiliate-disclosure-block">
      <p className="font-display text-sm font-bold">Amazon affiliate disclosure</p>
      <p className="mt-1 text-xs text-[var(--brand-body)]">
        Rendered as a small line of text under the public product grid
        (anywhere the <code>[[products]]</code> shortcode is used). Amazon
        Associates&apos; Operating Agreement requires this disclosure on
        every page that displays affiliate links — leave the default unless
        you have a reason to change it. Leaving this <em>blank</em> hides the
        line entirely; only do that if you&apos;ve put a disclosure elsewhere
        on the page.
      </p>
      <textarea
        value={value}
        onChange={(e) => setField("affiliate_disclosure")(e.target.value)}
        maxLength={500}
        rows={3}
        data-testid="admin-affiliate-disclosure"
        placeholder="As an Amazon Associate, LAST earns from qualifying purchases."
        className="mt-3 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
      />
      <p className={`mt-2 text-xs ${empty ? "text-[#E63946]" : "text-[var(--brand-body)]"}`}>
        {empty
          ? "Empty — the disclosure line will be hidden. Make sure you have one elsewhere on the page."
          : `${value.trim().length} / 500 characters`}
      </p>
    </div>
  );
}


function SocialLinksBlock({ form, setField }) {
  const rows = [
    { id: "social_instagram_url", label: "Instagram",       placeholder: "https://www.instagram.com/yourhandle" },
    { id: "social_facebook_url",  label: "Facebook page",   placeholder: "https://www.facebook.com/yourpage" },
    { id: "social_x_url",         label: "X (Twitter)",     placeholder: "https://x.com/yourhandle" },
    { id: "social_whatsapp_url",  label: "WhatsApp Channel", placeholder: "https://whatsapp.com/channel/…" },
  ];
  return (
    <div className="rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
      <p className="font-display text-sm font-bold">Social links</p>
      <p className="mt-1 text-xs text-[var(--brand-body)]">
        Shown opposite the &ldquo;LAteST from LAST&rdquo; headline on the homepage.
        Leave a field blank to hide that icon. WhatsApp is for a Channel link
        (start with <code>https://whatsapp.com/channel/</code>).
      </p>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {rows.map((r) => (
          <label key={r.id} className="block">
            <span className="label-kicker text-[var(--brand-body)]">{r.label}</span>
            <input
              type="url"
              value={form[r.id] || ""}
              onChange={(e) => setField(r.id)(e.target.value)}
              placeholder={r.placeholder}
              maxLength={400}
              data-testid={`admin-${r.id.replaceAll("_","-")}`}
              className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#0A0F17]"
            />
          </label>
        ))}
      </div>
    </div>
  );
}


function FaviconUploadButton({ onUploaded, setError }) {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) {
      setError("Favicon too large — please keep under 1 MB.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd);
      const url = r.data?.url;
      if (url) onUploaded(url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onPick}
        disabled={uploading}
        data-testid="favicon-upload"
        className="inline-flex items-center gap-1.5 rounded-md border border-black/15 bg-white px-3 py-1.5 label-kicker hover:border-[#0A0F17]"
      >
        {uploading ? <Loader2 size={12} className="animate-spin" /> : null}
        {uploading ? "Uploading…" : "Upload favicon"}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/png, image/x-icon, image/svg+xml, image/jpeg, image/webp"
        className="hidden"
        onChange={onFile}
        data-testid="favicon-input"
      />
    </>
  );
}

function TextRow({ label, hint, value, onChange, maxLength, testid }) {
  return (
    <label className="block">
      <span className="label-kicker text-[var(--brand-body)]">{label}</span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        data-testid={testid}
        className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
      />
      {hint ? <p className="mt-1 text-xs text-[var(--brand-body)]">{hint}</p> : null}
    </label>
  );
}


function NotificationsSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    enabled: true,
    recipient_email: "",
    forward_link_suggestions: true,
  });
  const [original, setOriginal] = useState(form);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/settings/notifications");
        if (cancelled) return;
        const next = {
          enabled: !!r.data.enabled,
          recipient_email: r.data.recipient_email || "",
          forward_link_suggestions: !!r.data.forward_link_suggestions,
        };
        setForm(next);
        setOriginal(next);
      } catch (err) {
        if (!cancelled) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirty =
    form.enabled !== original.enabled ||
    form.recipient_email !== original.recipient_email ||
    form.forward_link_suggestions !== original.forward_link_suggestions;

  const validEmail = !form.recipient_email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.recipient_email.trim());

  const save = async (e) => {
    e?.preventDefault();
    if (!dirty || !validEmail) return;
    setSaving(true);
    setError("");
    try {
      const r = await api.put("/settings/notifications", {
        enabled: form.enabled,
        recipient_email: form.recipient_email.trim(),
        forward_link_suggestions: form.forward_link_suggestions,
      });
      const next = {
        enabled: !!r.data.enabled,
        recipient_email: r.data.recipient_email || "",
        forward_link_suggestions: !!r.data.forward_link_suggestions,
      };
      setForm(next);
      setOriginal(next);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="mt-6 space-y-5 rounded-md border border-black/10 bg-white p-6"
      data-testid="notifications-section"
    >
      <div className="flex items-center gap-2">
        <Mail size={16} className="text-[var(--brand-body)]" />
        <h2 className="font-display text-lg font-bold leading-none">Email notifications</h2>
      </div>
      <p className="text-xs text-[var(--brand-body)]">
        Forward every contact-form message (and optionally link suggestions) to
        an email address you choose. Messages still appear in the{" "}
        <RouterLink to="/admin/inbox" className="underline">Admin inbox</RouterLink>.
      </p>

      {error ? (
        <p
          className="rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
          data-testid="notifications-error"
        >
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-[var(--brand-body)]">Loading…</p>
      ) : (
        <>
          <ToggleRow
            label="Forward messages by email"
            hint="Master switch. Off = inbox only, no email sent."
            checked={form.enabled}
            onChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
            testid="notifications-enabled"
          />

          <div>
            <label className="label-kicker text-[var(--brand-body)]">Recipient email</label>
            <input
              type="email"
              value={form.recipient_email}
              onChange={(e) => setForm((f) => ({ ...f, recipient_email: e.target.value }))}
              placeholder="you@example.com"
              data-testid="notifications-recipient"
              className={`mt-2 block w-full rounded-md border bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17] ${
                validEmail ? "border-black/15" : "border-[#E63946]"
              }`}
            />
            <p className="mt-1 text-xs text-[var(--brand-body)]">
              While using the Resend test sender (<code>onboarding@resend.dev</code>),
              this MUST match the email you signed up to Resend with. Once you
              verify a custom domain at resend.com/domains, you can use any address.
            </p>
          </div>

          <ToggleRow
            label="Also forward link suggestions"
            hint="Submissions from the “Suggest a link” form on the public Links page."
            checked={form.forward_link_suggestions}
            onChange={(v) => setForm((f) => ({ ...f, forward_link_suggestions: v }))}
            testid="notifications-link-suggestions"
          />
        </>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-black/5 pt-4">
        {savedFlash ? (
          <span className="inline-flex items-center gap-1 label-kicker text-[#16A34A]">
            <CheckCircle2 size={12} /> Saved
          </span>
        ) : null}
        <button
          type="submit"
          disabled={!dirty || saving || !validEmail}
          data-testid="notifications-save"
          className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save notifications"}
        </button>
      </div>
    </form>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="font-display text-sm font-bold">{label}</p>
        {hint ? <p className="mt-0.5 text-xs text-[var(--brand-body)]">{hint}</p> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        data-testid={testid}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          checked ? "bg-[#0A0F17]" : "bg-black/15"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}


function FooterSection({ form, setForm }) {
  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="border-t border-black/10 pt-6" data-testid="footer-section">
      <p className="label-kicker text-[var(--brand-body)]">Site footer text</p>
      <p className="mt-1 text-xs text-[var(--brand-body)]">
        Edit or clear any line. Blank values hide that line entirely. Legal,
        Privacy, Archive and "Buy me a coffee" links remain.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-5">
        <label className="block">
          <span className="label-kicker text-[var(--brand-body)]">
            Footer title (left, bold)
          </span>
          <input
            type="text"
            data-testid="footer-title-input"
            value={form.footer_title ?? ""}
            maxLength={160}
            onChange={setField("footer_title")}
            className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 text-sm"
            placeholder="LAST — Let's All Sing Together"
          />
        </label>
        <label className="block">
          <span className="label-kicker text-[var(--brand-body)]">
            Footer description (the small paragraph)
          </span>
          <textarea
            data-testid="footer-description-input"
            value={form.footer_description ?? ""}
            maxLength={600}
            onChange={setField("footer_description")}
            rows={3}
            className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 text-sm"
            placeholder="An independent fan-built aggregator for Coventry City supporters…"
          />
          <span className="mt-1 block text-right text-xs text-[var(--brand-body)]">
            {(form.footer_description ?? "").length}/600
          </span>
        </label>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <label className="block">
            <span className="label-kicker text-[var(--brand-body)]">
              Right-side kicker
            </span>
            <input
              type="text"
              data-testid="footer-kicker-input"
              value={form.footer_kicker ?? ""}
              maxLength={120}
              onChange={setField("footer_kicker")}
              className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 text-sm"
              placeholder="Play up Sky Blues"
            />
          </label>
          <label className="block">
            <span className="label-kicker text-[var(--brand-body)]">
              Right-side tagline
            </span>
            <input
              type="text"
              data-testid="footer-tagline-input"
              value={form.footer_tagline ?? ""}
              maxLength={160}
              onChange={setField("footer_tagline")}
              className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 text-sm"
              placeholder="Built for the Coventry faithful · 2026"
            />
          </label>
        </div>
        <label className="block">
          <span className="label-kicker text-[var(--brand-body)]">
            Tagline link (optional)
          </span>
          <input
            type="text"
            data-testid="footer-tagline-url-input"
            value={form.footer_tagline_url ?? ""}
            maxLength={500}
            onChange={setField("footer_tagline_url")}
            className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 font-mono text-xs"
            placeholder="https://example.com or /page/about"
          />
          <span className="mt-1 block text-xs text-[var(--brand-body)]">
            Leave blank for plain text. External URLs (https://…) open in a new tab.
          </span>
        </label>
      </div>
    </div>
  );
}
